/**
 * Compute the full set of dynamic-route params SvelteKit should prerender.
 *
 * Why this exists (issue #63):
 * SvelteKit 2.x's prerender crawler is non-deterministic for routes whose
 * URLs are produced at render time (here: `getMatchPath` / `getTeamPath`,
 * which slugify contestant names off the live data). Two consecutive
 * `pnpm build`s can crawl a different subset of `/matches/[slugpath]/**`
 * and `/teams/[slug]/**`, producing builds that disagree on which HTML
 * files exist — even when the JS bundles are byte-identical (the #63
 * fix in `bin/build-version/index.js`).
 *
 * The fix: each dynamic route's `+page.js` exports `entries()` returning
 * the param list computed here. SvelteKit guarantees every returned entry
 * is prerendered, so the output set stops depending on crawler luck and
 * matches the data byte-for-byte.
 *
 * Single source of truth (publish-flake fix):
 * Every slug we emit is read DIRECTLY from the same primitives the
 * dynamic routes resolve against — no slug logic lives here:
 *
 *   • `getData()`  — the runtime data fetcher (URL resolution,
 *                    `parseData`, `decorateMatchPath`, schema validation)
 *   • `getTeams()` — exposes `team.slug` exactly as the team route's
 *                    `+layout.js` builds it
 *   • `match.path` — set by `decorateMatchPath` inside `getData()`, the
 *                    same URL the runtime UI links to
 *
 * Changing fetch / parse / slug logic happens in one place — the data
 * layer / team helper. Prerender follows automatically.
 *
 * The previous implementation read from a bundled
 * `src/statics/data/current/matches.json` snapshot and re-implemented
 * slug computation. The snapshot could drift from the live data on S3
 * between commits; on publish runs the entry list and the SSR data
 * then disagreed and the team-page `+layout.js` crashed in `getSquad()`
 * with `Cannot read properties of null (reading 'id')` for any slug
 * the live data didn't carry.
 *
 * `entries()` runs at build time in Node — there's no SvelteKit-scoped
 * `fetch`. We pass a small `buildFetch` shim that mirrors the two URL
 * shapes `getUrl()` produces:
 *   - absolute `https://…` → use Node's global `fetch`
 *   - root-relative `/data/…` (dev / preview-build local-data path) →
 *     read directly from `src/statics/<path>`
 *
 * On absolute-URL failures the shim throws — `safeFetchJson` would
 * otherwise swallow the error and `getData()` would silently return
 * empty fallback data, producing zero prerender entries. That's the
 * silent-mismatch class of bug this module exists to prevent.
 */

import { getData } from '$utils/data';
import getTeams from '$utils/getTeams';

/**
 * Build-time fetch shim. Handles the two URL shapes `src/utils/data/urls.js`
 * produces from `getUrl()`:
 *
 *   - absolute (`https://graphics.thomsonreuters.com/…`) — the production
 *     publish path, served from S3. Use Node's global `fetch`.
 *   - root-relative (`/data/<folder>/matches.json`) — the dev / preview
 *     local-data path, normally served by SvelteKit's dev server. At
 *     build time that server isn't running yet, so read straight from
 *     `src/statics/<path>` on disk.
 *
 * Returned objects are minimal `Response`-shaped — only `ok`, `status`,
 * `statusText`, `text()` and `json()` are used by `safeFetchJson`.
 *
 * @param {string} url
 * @returns {Promise<Response>}
 */
async function buildFetch(url) {
  if (/^https?:\/\//i.test(url)) {
    const res = await fetch(url);
    // `getData()` routes its primary `matches.json` request through
    // here. `safeFetchJson` would normally swallow a non-OK response
    // and silently fall back to bundled empty data — which would
    // produce zero prerender entries on a publish run when S3 is
    // unreachable, exactly the silent-mismatch failure this module
    // exists to prevent. Throw loudly instead.
    if (!res.ok) {
      throw new Error(
        `getPrerenderEntries: failed to fetch ${url} ` +
          `(HTTP ${res.status} ${res.statusText}). ` +
          `Refusing to fall back to stale bundled data — fix the ` +
          `data source and re-run the build.`
      );
    }
    return res;
  }

  // Root-relative — read from src/statics/<path> directly.
  const [{ readFile }, { fileURLToPath }, path] = await Promise.all([
    import('node:fs/promises'),
    import('node:url'),
    import('node:path'),
  ]);
  const here = path.dirname(fileURLToPath(import.meta.url));
  const file = path.resolve(
    here,
    '..',
    '..',
    'statics',
    url.replace(/^\/+/, '')
  );

  try {
    const text = await readFile(file, 'utf8');
    return new Response(text, {
      status: 200,
      headers: { 'content-type': 'application/json' },
    });
  } catch (e) {
    // Match `safeFetchJson`'s expected failure shape so it falls through
    // to its own fallback handling.
    return new Response('', { status: 404, statusText: 'Not Found' });
  }
}

// Cache the resolved `getData()` payload across all callers (4: team +
// team-share + match + match-share `entries()`), so one publish build
// hits the data source once instead of four times.
/** @type {Promise<object> | undefined} */
let dataPromise;

const loadData = () => {
  if (!dataPromise) dataPromise = getData({ fetch: buildFetch });
  return dataPromise;
};

/**
 * `<home>-<away>-MM-DD` slugpaths for the `/matches/[slugpath]` route.
 * Reads `match.path` straight off each match (set by `decorateMatchPath`
 * inside `getData()`), strips the `matches/` prefix and trailing slash
 * off so it matches `params.slugpath`. Matches with placeholder
 * contestants set `path` to `'.'`, which we filter out — no real page
 * exists yet.
 *
 * @returns {Promise<string[]>}
 */
export async function getMatchSlugpaths() {
  const data = await loadData();
  const matches = data?.matches?.matches?.match ?? [];
  const slugpaths = new Set();
  for (const match of matches) {
    const path = /** @type {{ path?: string }} */ (match).path;
    if (!path || path === '.') continue;
    const m = path.match(/^matches\/(.+?)\/?$/);
    if (m) slugpaths.add(m[1]);
  }
  return [...slugpaths].sort();
}

/**
 * Team slugs for the `/teams/[slug]` route. Delegates to `getTeams()` —
 * the same helper the team route's `+layout.js` uses to resolve
 * `params.slug` — so the prerender list and the runtime lookup can't
 * disagree about which slugs exist or how names are slugified.
 *
 * @returns {Promise<string[]>}
 */
export async function getTeamSlugs() {
  const data = await loadData();
  const teams = getTeams(data?.matches);
  return teams.map((t) => t.slug).sort();
}
